/**
 * Mutation hooks for matter actions on the case detail panel — the firm-
 * decision + routing-removal bands AND (slice 5c) the lead-level actions.
 * Name kept as `usePlacementAction` for caller-stability; lead-side mutations
 * live here too because the panel reads from one source.
 *
 *   usePlacementAction().accept           → POST /api/placements/:id/accept
 *   usePlacementAction().decline          → POST /api/placements/:id/decline  { reason }
 *   usePlacementAction().pull             → POST /api/placements/:id/pull
 *   usePlacementAction().closeCase        → POST /api/placements/:id/close (slice 5a)
 *   usePlacementAction().confirmLfPayment → POST /api/placements/:id/confirm-lf-payment (5b)
 *   usePlacementAction().confirmOfPayment → POST /api/placements/:id/confirm-of-payment (5b)
 *   usePlacementAction().editSettlement   → POST /api/placements/:id/edit-settlement (5b)
 *   usePlacementAction().reject           → POST /api/leads/:id/reject  { reason } (5c)
 *   usePlacementAction().editClient       → POST /api/leads/:id/edit-client { ...fields } (5c)
 *   usePlacementAction().fetchLead        → GET  /api/leads/:id (5c — edit-client form prefill)
 *
 * A firm-decision change moves the matter between books (accept lifts a
 * circulating lead into Accepted book; decline/pull drop it back to a stuck/reroute
 * state, or — three deep — reject it downstream). Rather than reason about every
 * destination, the writes invalidate all matter-bearing book queries + the
 * Surface, so whichever list the matter lands in refreshes on the next render.
 * Broad-but-cheap, matching the attention layer's invalidation (small surface,
 * two operators). The 15s poll is the backstop for any async n8n follow-on.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { EligibleFirmsResponse, Lead } from "@command-center/shared";
import { apiGet, apiPost } from "./api";

/** Every matter-bearing list a firm-decision change can ripple into. */
const BOOK_KEYS = [
  ["under-review"],
  ["accepted"],
  ["placed"],
  ["settled"],
  ["dropped"],
  ["closed"],
  ["rejected"],
  ["intake-verification"],
  ["surface"],
] as const;

export function usePlacementAction() {
  const queryClient = useQueryClient();
  const invalidateBooks = () => {
    for (const queryKey of BOOK_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };

  const accept = useMutation<unknown, Error, { placementId: string }>({
    mutationFn: ({ placementId }) =>
      apiPost(`/api/placements/${encodeURIComponent(placementId)}/accept`, {}),
    onSuccess: invalidateBooks,
  });

  const decline = useMutation<unknown, Error, { placementId: string; reason: string }>({
    mutationFn: ({ placementId, reason }) =>
      apiPost(`/api/placements/${encodeURIComponent(placementId)}/decline`, { reason }),
    onSuccess: invalidateBooks,
  });

  const pull = useMutation<unknown, Error, { placementId: string }>({
    mutationFn: ({ placementId }) =>
      apiPost(`/api/placements/${encodeURIComponent(placementId)}/pull`, {}),
    onSuccess: invalidateBooks,
  });

  // Close-case: bodyless, mirrors pull's shape. Stamps Case Closed Date —
  // the matter moves from the Placed/Settled book to Closed, so the same
  // broad invalidation pattern applies.
  // close_reason is OPTIONAL (ADR-0075): send the key only when the operator
  // typed one, so a blank close posts `{}` (the route's bodyless path).
  const closeCase = useMutation<
    unknown,
    Error,
    { placementId: string; close_reason?: string }
  >({
    mutationFn: ({ placementId, close_reason }) =>
      apiPost(
        `/api/placements/${encodeURIComponent(placementId)}/close`,
        close_reason ? { close_reason } : {},
      ),
    onSuccess: invalidateBooks,
  });

  // Slice 5b — settlement-band actions on Settled book placements. None of
  // them moves the matter between books (the placement stays Settled), but
  // we invalidate the same broad set: a settlement amount edit or a payment
  // confirmation changes the panel + Settled book row immediately, and the
  // stat-card summary count is unaffected. The broad invalidation is cheap
  // and removes a class of stale-projection bugs.
  const confirmLfPayment = useMutation<unknown, Error, { placementId: string }>({
    mutationFn: ({ placementId }) =>
      apiPost(
        `/api/placements/${encodeURIComponent(placementId)}/confirm-lf-payment`,
        {},
      ),
    onSuccess: invalidateBooks,
  });

  const confirmOfPayment = useMutation<unknown, Error, { placementId: string }>({
    mutationFn: ({ placementId }) =>
      apiPost(
        `/api/placements/${encodeURIComponent(placementId)}/confirm-of-payment`,
        {},
      ),
    onSuccess: invalidateBooks,
  });

  // Body carries any subset of the 3 settlement fields. Undefined keys are
  // omitted in JSON.stringify so the wire body matches the optional schema
  // exactly — the route trusts the validated body, the writer trusts the
  // route.
  const editSettlement = useMutation<
    unknown,
    Error,
    {
      placementId: string;
      settlementAmount?: number;
      attorneyFeeRate?: number;
      settlementDate?: string;
    }
  >({
    mutationFn: ({ placementId, ...fields }) =>
      apiPost(
        `/api/placements/${encodeURIComponent(placementId)}/edit-settlement`,
        fields,
      ),
    onSuccess: invalidateBooks,
  });

  // Slice 5c — lead-level actions. Both mutate the Lead record (not a
  // Placement under it), so they route through /api/leads/:id. Reject
  // additionally cascades placement declines server-side — that's why the
  // broad book invalidation matters here: the lead may be cleared from
  // Under Review book / Accepted book AND placements may move into Rejected on a single
  // call. Edit-client typically doesn't move books, but a `signed` flip or
  // `incidentState`-on-Unmatched side effect can — invalidate broadly,
  // same posture.
  const reject = useMutation<unknown, Error, { leadId: string; reason: string }>(
    {
      mutationFn: ({ leadId, reason }) =>
        apiPost(`/api/leads/${encodeURIComponent(leadId)}/reject`, { reason }),
      onSuccess: invalidateBooks,
    },
  );

  const editClient = useMutation<
    unknown,
    Error,
    {
      leadId: string;
      name?: string;
      phone?: string;
      phone2?: string;
      email?: string;
      caseSummary?: string;
      caseType?: string;
      incidentDate?: string;
      incidentState?: string;
      residentState?: string;
      address?: string;
      intakeDocs?: unknown[];
      internalNotes?: string;
      signed?: boolean;
    }
  >({
    mutationFn: ({ leadId, ...fields }) =>
      apiPost(
        `/api/leads/${encodeURIComponent(leadId)}/edit-client`,
        fields,
      ),
    onSuccess: invalidateBooks,
  });

  // Edit-client form prefill — fetches the live Lead so the form opens with
  // current values regardless of which book opened the panel. Not a
  // mutation; exposed as a plain async function rather than a useQuery so
  // the panel can call it imperatively on Edit Client click (the form lives
  // inside the footer; a useQuery would over-fetch for cases where Edit
  // Client is never clicked).
  // Stable identity (useCallback) so the footer effects that depend on these
  // fetches don't re-fire on every parent render — see F-010.
  const fetchLead = useCallback(
    (leadId: string): Promise<Lead> =>
      apiGet<Lead>(`/api/leads/${encodeURIComponent(leadId)}`),
    [],
  );

  // Slice 5d — route + reroute. Both mint a new Placement (Decision=Assigned)
  // and flip Lead.Status to "Under Review". Same broad invalidation: a
  // Pending lead leaves the Under Review book's misc list, a routed transfer
  // appears in Under Review book's lead list, and a rerouted placement appears at
  // the new firm. The firm-picker modal calls one of the two fetch
  // functions below to populate its eligibility list before triggering
  // the mutation.
  const route = useMutation<
    unknown,
    Error,
    { leadId: string; firmId: string; eligibilityOverride?: boolean }
  >({
    mutationFn: ({ leadId, firmId, eligibilityOverride }) =>
      apiPost(`/api/leads/${encodeURIComponent(leadId)}/route`, {
        firmId,
        ...(eligibilityOverride !== undefined ? { eligibilityOverride } : {}),
      }),
    onSuccess: invalidateBooks,
  });

  const reroute = useMutation<
    unknown,
    Error,
    {
      declinedPlacementId: string;
      firmId: string;
      eligibilityOverride?: boolean;
    }
  >({
    mutationFn: ({ declinedPlacementId, firmId, eligibilityOverride }) =>
      apiPost(
        `/api/placements/${encodeURIComponent(declinedPlacementId)}/reroute`,
        {
          firmId,
          ...(eligibilityOverride !== undefined ? { eligibilityOverride } : {}),
        },
      ),
    onSuccess: invalidateBooks,
  });

  /**
   * Fetch eligible Handling Firms for a Pending signed transfer (route
   * action). Imperative — only fires when the operator clicks Route, so
   * the heavy Firms + Placements read doesn't run for every panel open.
   */
  const fetchEligibleFirmsForRoute = useCallback(
    (leadId: string): Promise<EligibleFirmsResponse> =>
      apiGet<EligibleFirmsResponse>(
        `/api/leads/${encodeURIComponent(leadId)}/eligible-firms`,
      ),
    [],
  );

  /**
   * Fetch eligible HFs for a Declined/Pulled placement (reroute action).
   * Resolves the linked lead server-side and returns the candidate list
   * for it, with the firm that just declined annotated `previouslyDeclined`.
   */
  const fetchEligibleFirmsForReroute = useCallback(
    (declinedPlacementId: string): Promise<EligibleFirmsResponse> =>
      apiGet<EligibleFirmsResponse>(
        `/api/placements/${encodeURIComponent(declinedPlacementId)}/reroute-candidates`,
      ),
    [],
  );

  return {
    accept,
    decline,
    pull,
    closeCase,
    confirmLfPayment,
    confirmOfPayment,
    editSettlement,
    reject,
    editClient,
    fetchLead,
    route,
    reroute,
    fetchEligibleFirmsForRoute,
    fetchEligibleFirmsForReroute,
  };
}
